import { SavedFilterOptions, SavedSortOptions } from 'common/hooks';
import {
  GrowerBlock,
  PaginatedResult,
  PaginationQueryParams,
} from 'common/models';
import { DateWithoutTime } from 'common/models/dateType';
import { cmsApi } from '../cmsApi';
import {
  BlockDetails,
  RecentlyUpdatedBlockDetails,
  WithAllSeasonData,
  WithPartialSeasonData,
} from '../dto/get-block.dto';
import {
  BlockEvalData,
  UpdateBlockStatusDto,
  UpdateDetailsDto,
  UpdateEvalDto,
  UpdateLatestEstimateDto,
  UpdateMonitoringDto,
  UpdateSizesDto,
} from '../dto/growerBlockApiTypes';
import { withSortsAndFilters } from '../utils/apiFunctions';
import { shouldForceRefetch } from '../utils/handleExpiredSession';
import { cacheIdFormatters as growerCacheFormatters } from './grower';

const blocksBaseUrl = '/grower-blocks';
const withEvaluationPath = 'evaluation';

type BlockSortFilterQueryParams = {
  sorts: SavedSortOptions[];
  filters: SavedFilterOptions[];
};

type SeasonAwareBlock = {
  seasonId: number;
  blockId: number;
};

type OptionalGrower = {
  /**
   * The identifier of the grower to which the block belongs.
   *
   * @remarks
   * This is optional because it's only used for cache management purposes. If
   * not provided, all individual growers recently obtained will be invalidated.
   */
  growerId?: number;
};

type UpdateBlockStatusData = UpdateBlockStatusDto &
  SeasonAwareBlock &
  OptionalGrower;

type UpdateBlockEstimateData = UpdateLatestEstimateDto &
  SeasonAwareBlock &
  OptionalGrower;

const blockListIdFormatter = (seasonId: number) => seasonId;
export const cacheIdFormatters = {
  block: (
    seasonId: number,
    blockId: number,
    details: 'basic' | 'all' = 'basic',
  ) =>
    `${
      details === 'all' ? `${withEvaluationPath}/` : ''
    }${seasonId}/${blockId}`,
  recentBlocks: blockListIdFormatter,
  harvestableBlocks: blockListIdFormatter,
  pendingBlocks: blockListIdFormatter,
};

const withGrowerBlockEndpoints = cmsApi.injectEndpoints({
  endpoints: builder => ({
    getPaginatedPartialBlockDetails: builder.query<
      PaginatedResult<RecentlyUpdatedBlockDetails>,
      PaginationQueryParams & BlockSortFilterQueryParams & { seasonId: number }
    >({
      query: ({ seasonId, ...prm }) =>
        withSortsAndFilters(prm, `${blocksBaseUrl}/list/${seasonId}`),
      providesTags: (_, err, args) =>
        err
          ? []
          : [
              {
                type: 'RecentBlocks',
                id: cacheIdFormatters.recentBlocks(args.seasonId),
              },
            ],
      forceRefetch: shouldForceRefetch,
    }),

    getUnpaginatedPartialBlockDetails: builder.query<
      RecentlyUpdatedBlockDetails[],
      BlockSortFilterQueryParams & { seasonId: number }
    >({
      query: ({ seasonId, ...prm }) =>
        withSortsAndFilters(prm, `${blocksBaseUrl}/${seasonId}`),
      forceRefetch: () => true, // Always refetch to get the latest data.
    }),

    getAllOrOtherBlocksWithEvaluation: builder.query<
      PaginatedResult<BlockDetails<WithAllSeasonData>>,
      PaginationQueryParams & BlockSortFilterQueryParams & { seasonId: number }
    >({
      query: ({ seasonId, sorts, ...prm }) =>
        withSortsAndFilters(
          { embeddedSorts: sorts, ...prm },
          `${blocksBaseUrl}/${withEvaluationPath}/list/${seasonId}`,
        ),
      providesTags: (_, err, args) =>
        err
          ? []
          : [
              {
                type: 'HarvestableBlocks',
                id: cacheIdFormatters.harvestableBlocks(args.seasonId),
              },
            ],
      forceRefetch: shouldForceRefetch,
    }),

    getBlocksAssignedToFieldRep: builder.query<
      PaginatedResult<BlockDetails<WithAllSeasonData>>,
      PaginationQueryParams & BlockSortFilterQueryParams & { seasonId: number }
    >({
      query: ({ seasonId, sorts, ...prm }) =>
        withSortsAndFilters(
          { embeddedSorts: sorts, ...prm },
          `${blocksBaseUrl}/${withEvaluationPath}/list/assigned/${seasonId}`,
        ),
      providesTags: (_, err, args) =>
        err
          ? []
          : [
              {
                type: 'HarvestableBlocks',
                id: cacheIdFormatters.harvestableBlocks(args.seasonId),
              },
            ],
      forceRefetch: shouldForceRefetch,
    }),

    getBlocksPendingEvaluation: builder.query<
      PaginatedResult<BlockDetails<WithAllSeasonData>>,
      PaginationQueryParams & BlockSortFilterQueryParams & { seasonId: number }
    >({
      query: ({ seasonId, sorts, ...prm }) =>
        withSortsAndFilters(
          { embeddedSorts: sorts, ...prm },
          `${blocksBaseUrl}/${withEvaluationPath}/list/pending/${seasonId}`,
        ),
      providesTags: (_, err, args) =>
        err
          ? []
          : [
              {
                type: 'PendingBlocks',
                id: cacheIdFormatters.pendingBlocks(args.seasonId),
              },
            ],
      forceRefetch: shouldForceRefetch,
    }),

    getBlocksToSchedule: builder.query<
      PaginatedResult<BlockDetails<WithAllSeasonData>>,
      PaginationQueryParams &
        BlockSortFilterQueryParams & {
          seasonId: number;
          varietyId: number;
          pickDay: DateWithoutTime;
          packHouseId: number;
        }
    >({
      query: ({ seasonId, varietyId, pickDay, packHouseId, ...psfParams }) =>
        withSortsAndFilters(
          psfParams,
          `${blocksBaseUrl}/schedule/${seasonId}/${varietyId}/${pickDay}/${packHouseId}`,
        ),
      providesTags: (_, err, { seasonId, varietyId, pickDay, packHouseId }) =>
        err
          ? []
          : [
              {
                type: 'BlocksToSchedule',
                id: `${seasonId}/${varietyId}/${pickDay}/${packHouseId}`,
              },
            ],
      forceRefetch: shouldForceRefetch,
    }),

    getBlockWithBaseDetails: builder.query<
      BlockDetails<WithPartialSeasonData>,
      { blockId: number; seasonId: number }
    >({
      query: ({ seasonId, blockId }) =>
        `${blocksBaseUrl}/${seasonId}/${blockId}`,
      providesTags: (_, err, { seasonId, blockId }) =>
        err
          ? []
          : [
              {
                type: 'Block',
                id: cacheIdFormatters.block(seasonId, blockId),
              },
            ],
      forceRefetch: shouldForceRefetch,
    }),

    getBlockWithAllDetails: builder.query<
      BlockDetails<WithAllSeasonData>,
      { id: number; seasonId: number }
    >({
      query: ({ seasonId, id }) =>
        `${blocksBaseUrl}/${withEvaluationPath}/${seasonId}/${id}`,
      providesTags: (_, err, { seasonId, id }) =>
        err
          ? []
          : [
              {
                type: 'Block',
                id: `${withEvaluationPath}/${seasonId}/${id}`,
              },
            ],
      forceRefetch: shouldForceRefetch,
    }),

    getBlockSeasonEvalData: builder.query<
      BlockEvalData[],
      BlockSortFilterQueryParams & { seasonId: number }
    >({
      query: ({ seasonId, sorts, ...psfParams }) =>
        withSortsAndFilters(
          { embeddedSorts: sorts, ...psfParams },
          `${blocksBaseUrl}/evaluation-data/${seasonId}`,
        ),
      // Since this endpoint is for CSV export, we always want the latest data.
      forceRefetch: () => true,
    }),

    addToSeason: builder.mutation<
      WithAllSeasonData,
      SeasonAwareBlock & OptionalGrower
    >({
      query: ({ seasonId, blockId }) => ({
        url: `${blocksBaseUrl}/season/${seasonId}/${blockId}`,
        method: 'POST',
      }),
      invalidatesTags: (_, err, { seasonId, blockId, growerId }) =>
        err
          ? []
          : [
              { type: 'RecentBlocks', id: seasonId },
              { type: 'Block', id: cacheIdFormatters.block(seasonId, blockId) },
              {
                type: 'Grower',
                id:
                  growerId && growerCacheFormatters.grower(seasonId, growerId),
              },
              {
                type: 'Growers',
                id: growerCacheFormatters.growers(seasonId),
              },
            ],
    }),

    updateBlockStatus: builder.mutation<
      WithAllSeasonData,
      UpdateBlockStatusData
    >({
      query: ({ seasonId, blockId, ...payload }) => ({
        url: `${blocksBaseUrl}/status/${seasonId}/${blockId}`,
        method: 'PUT',
        body: payload,
      }),
      invalidatesTags: (_, err, { seasonId, blockId, growerId }) =>
        err
          ? []
          : [
              'RecentBlocks',
              'HarvestableBlocks',
              'PendingBlocks',
              'BlocksToSchedule',
              {
                type: 'Block',
                id: cacheIdFormatters.block(seasonId, blockId, 'basic'),
              },
              {
                type: 'Block',
                id: cacheIdFormatters.block(seasonId, blockId, 'all'),
              },
              {
                type: 'Grower',
                id:
                  growerId && growerCacheFormatters.grower(seasonId, growerId),
              },
              {
                type: 'Growers',
                id: growerCacheFormatters.growers(seasonId),
              },
            ],
    }),

    updateBlock: builder.mutation<
      WithAllSeasonData,
      UpdateDetailsDto & { seasonId: number; blockId: number }
    >({
      query: ({ seasonId, blockId, ...payload }) => ({
        url: `${blocksBaseUrl}/${seasonId}/${blockId}`,
        method: 'PUT',
        body: payload,
      }),
      // Refetch all blocks as well as the block with the updated details.
      invalidatesTags: (_, err, { seasonId, blockId }) =>
        err
          ? []
          : [
              'RecentBlocks',
              'HarvestableBlocks',
              'PendingBlocks',
              'BlocksToSchedule',
              {
                type: 'Block',
                id: cacheIdFormatters.block(seasonId, blockId),
              },
              {
                type: 'Block',
                id: cacheIdFormatters.block(seasonId, blockId, 'all'),
              },
            ],
    }),

    updateEvaluation: builder.mutation<
      WithAllSeasonData,
      UpdateEvalDto & { seasonId: number; blockId: number }
    >({
      query: ({ seasonId, blockId, ...payload }) => ({
        url: `${blocksBaseUrl}/${withEvaluationPath}/${seasonId}/${blockId}`,
        method: 'PUT',
        body: payload,
      }),
      // Refetch all blocks as well as the block with the updated evaluation.
      invalidatesTags: (_, err, { seasonId, blockId }) =>
        err
          ? []
          : [
              'RecentBlocks',
              'HarvestableBlocks',
              'PendingBlocks',
              'BlocksToSchedule',
              {
                type: 'Block',
                id: cacheIdFormatters.block(seasonId, blockId),
              },
              {
                type: 'Block',
                id: cacheIdFormatters.block(seasonId, blockId, 'all'),
              },
            ],
    }),

    updateSizes: builder.mutation<
      GrowerBlock,
      UpdateSizesDto & { seasonId: number; blockId: number }
    >({
      query: ({ seasonId, blockId, sizeEstimates }) => ({
        url: `${blocksBaseUrl}/size-estimates/${seasonId}/${blockId}`,
        method: 'PUT',
        body: { sizeEstimates },
      }),
      // Refetch the block with the updated sizes.
      invalidatesTags: (_, err, { seasonId, blockId }) =>
        err
          ? []
          : [
              'RecentBlocks',
              'HarvestableBlocks',
              'PendingBlocks',
              'BlocksToSchedule',
              {
                type: 'Block',
                id: cacheIdFormatters.block(seasonId, blockId, 'all'),
              },
            ],
    }),

    updateLatestEstimate: builder.mutation<
      WithAllSeasonData,
      UpdateBlockEstimateData
    >({
      query: ({ seasonId, blockId, ...payload }) => ({
        url: `${blocksBaseUrl}/latest-estimate/${seasonId}/${blockId}`,
        method: 'PUT',
        body: payload,
      }),
      invalidatesTags: (_, err, { seasonId, blockId, growerId }) =>
        err
          ? []
          : [
              'RecentBlocks',
              'HarvestableBlocks',
              'PendingBlocks',
              'BlocksToSchedule',
              {
                type: 'Block',
                id: cacheIdFormatters.block(seasonId, blockId),
              },
              {
                type: 'Block',
                id: cacheIdFormatters.block(seasonId, blockId, 'all'),
              },
              {
                type: 'Grower',
                id:
                  growerId && growerCacheFormatters.grower(seasonId, growerId),
              },
              {
                type: 'Growers',
                id: growerCacheFormatters.growers(seasonId),
              },
            ],
    }),

    updateMonitoring: builder.mutation<
      WithAllSeasonData,
      UpdateMonitoringDto & { seasonId: number; blockId: number }
    >({
      query: ({ seasonId, blockId, ...payload }) => ({
        url: `${blocksBaseUrl}/monitoring/${seasonId}/${blockId}`,
        method: 'PUT',
        body: payload,
      }),
      // Refetch the block with the updated orchard monitoring.
      invalidatesTags: (_, err, { seasonId, blockId }) =>
        err
          ? []
          : [
              {
                type: 'Block',
                id: cacheIdFormatters.block(seasonId, blockId),
              },
              {
                type: 'Block',
                id: cacheIdFormatters.block(seasonId, blockId, 'all'),
              },
            ],
    }),
  }),
});

export const {
  useGetPaginatedPartialBlockDetailsQuery,
  useGetUnpaginatedPartialBlockDetailsQuery,
  useGetBlocksAssignedToFieldRepQuery,
  useGetAllOrOtherBlocksWithEvaluationQuery,
  useGetBlocksPendingEvaluationQuery,
  useGetBlocksToScheduleQuery,
  useGetBlockSeasonEvalDataQuery,
  useGetBlockWithBaseDetailsQuery,
  useGetBlockWithAllDetailsQuery,
  useAddToSeasonMutation,
  useUpdateBlockStatusMutation,
  useUpdateBlockMutation,
  useUpdateEvaluationMutation,
  useUpdateLatestEstimateMutation,
  useUpdateSizesMutation,
  useUpdateMonitoringMutation,
} = withGrowerBlockEndpoints;
